import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import DropdownInput from "../../../components/DropdownInput";
import {API} from "../../../types/API";
import {AxiosResponse} from "axios";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import CountryAvatar from "../../../components/CountryAvatar";
import {WithdrawProvider} from "../../../types/WithdrawProvider";
import {WithdrawCountryResponse} from "../../../types/Responses/WithdrawsResponse";
import {useSystemContext} from "../../../types/contexts/SystemContext";
import OutlineInput from "../../../components/OutlineInput";
import {Country} from "../../../types/Responses/CountriesResponse";
import {Button} from "@material-tailwind/react";
import Loader from "../../../assets/icons/Loader";
import {InputType} from "../../../types/InputType";
import {validateData} from "../../../reducers/validationReducer";
import {useDispatch} from "react-redux";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {emailRegex} from "./WithdrawsForm";
import {useTranslation} from "react-i18next";
import {APILink} from "../../../types/APILink";

interface SafeChargeFormProps {
    broker: number,
    clientTransactionID?: number
}

const SafeChargeForm = ({broker, clientTransactionID}: SafeChargeFormProps) => {

    const {t} = useTranslation();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const system = useSystemContext();
    const [method, setMethod] = useState<string>();
    const [country, setCountry] = useState<number>();
    const [currency, setCurrency] = useState<number>();
    const [amount, setAmount] = useState<number>();
    const [firstName, setFirstName] = useState<string>();
    const [lastName, setLastName] = useState<string>();
    const [email, setEmail] = useState<string>();
    const [accountNumber, setAccountNumber] = useState<string>();

    const [validated, setValidated] = useState<boolean>(false);
    const [isInProgress, setInProgress] = useState<boolean>(false);

    const dispatch = useDispatch();
    const [countriesList, setCountriesList] = useState<WithdrawCountryResponse>();
    const methods: FooterListItem[] = [
        {name: 'PayRetails Payout', code: 'payretailer'},
        {name: 'LATAM Payout', code: 'latam'},
        {name: 'PIX Payout', code: 'pix'},
    ]

    const allowedMethodData = [
        {code: 'pix', countries: ['BR'], currencies: ['BRL']}
    ];

    const clearData = useCallback(() => {
        setInProgress(false);
        setMethod(undefined);
        setCountry(undefined);
        setCurrency(undefined);
        setAmount(undefined);
        setFirstName(undefined);
        setLastName(undefined);
        setEmail(undefined);
        setAccountNumber(undefined);
    }, []);

    const getCountries = useCallback(() => {
        API.getWithdrawCountries(broker, WithdrawProvider.SAFECHARGE).then((response: AxiosResponse) => {
            setCountriesList(response.data as WithdrawCountryResponse);
        }).catch(e => ErrorHandler(e,`get${APILink.GET_WITHDRAW_COUNTRIES}`, toast, handleError));
    }, [toast, handleError]);

    const getClientTransaction = useCallback(() => {
        API.getWithdrawTransaction(clientTransactionID ?? 0).then((response: AxiosResponse) => {
            setFirstName(response.data.client.first_name);
            setLastName(response.data.client.last_name);
            setEmail(response.data.client.email);
            setCurrency(response.data.currency.id);
            setCountry(response.data.country.id);
            setMethod('pix');
            if (response.data.transaction_data && response.data.transaction_data.pix_accountNum) {
                setAccountNumber(response.data.transaction_data.pix_accountNum)
            }
        }).catch(e => ErrorHandler(e, `get${APILink.GET_WITHDRAW_TRANSACTION.replace('$transaction', (clientTransactionID ?? 0).toString())}`,toast, handleError));
    }, []);

    const changeMethod = useCallback((s: string | number) => {
        if (!system || !system.system || !system.system.currencies || !countriesList)
            return;
        const m = typeof s !== "string" ? s.toString() : s;
        setMethod(m);
        const allowedMethod = allowedMethodData.find(a => a.code === m);
        if (allowedMethod && allowedMethod.countries.length == 1) {
            setCountry(countriesList.data.find(c => c.code === allowedMethod.countries[0])?.id ?? undefined);
            if (allowedMethod.currencies.length === 1)
                setCurrency(system.system.currencies.find(c => c.code === allowedMethod.currencies[0])?.id ?? undefined)
        } else {
            setCountry(undefined);
            setCurrency(undefined);
        }
        setFirstName(undefined)
        setLastName(undefined)
        setEmail(undefined)
        setAccountNumber(undefined)
        setAmount(0);
        setValidated(false);
    }, [setMethod, system, countriesList]);

    const changeCountry = useCallback((u: string | number) => {
        setCountry(typeof u === "string" ? parseInt(u) : u);
        setCurrency(undefined)
        setFirstName(undefined)
        setLastName(undefined)
        setEmail(undefined)
        setAccountNumber(undefined)
        setAmount(0);
        setValidated(false);
    }, [setCountry]);

    const changeCurrency = useCallback((u: string | number) => {
        setCurrency(typeof u === "string" ? parseInt(u) : u);
        setAmount(0);
        setFirstName(undefined)
        setLastName(undefined)
        setEmail(undefined)
        setAccountNumber(undefined)
        setValidated(false);
    }, [setCurrency]);

    const changeAmount = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAmount(parseFloat(e.currentTarget.value))
        }
    }, [setValidated, setAmount]);

    const changeFirstName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setFirstName(e.currentTarget.value)
        }
    }, [setValidated, setFirstName]);

    const changeLastName = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setLastName(e.currentTarget.value)
        }
    }, [setValidated, setLastName]);


    const changeEmail = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setEmail(e.currentTarget.value)
        }
    }, [setValidated, setEmail]);

    const changeAccountNumber = useCallback((e: React.ChangeEvent<HTMLInputElement> | undefined) => {
        if (e?.currentTarget) {
            setValidated(false);
            setAccountNumber(e.currentTarget.value)
        }
    }, [setValidated, setAccountNumber]);

    const handleSuccessResponse = useCallback((response: AxiosResponse) => {
        if (response.data.data) {
            clearData();
            const status = response.data.data.status;
            let message = '';
            if (status && status === 'Waiting for approve')
                message = t('admin.withdraws.approve_is_required')
            toast?.show(<><FontAwesomeIcon icon={solid("circle-check")}
                                           className="mr-4"/> {t('admin.withdraws.created_message')}.<span
                className="uppercase inline-block ml-1">{message}</span></>, "bg-teal-500");
        }
    }, [toast, clearData]);

    const callback = useCallback((b: boolean) => {
        setValidated(true);
        if (b) {
            let request = {amount, method, provider: WithdrawProvider.SAFECHARGE, currency, country, broker};
            if (method) {
                request = {
                    ...request,
                    ['email' as keyof typeof request]: email,
                    ['first_name' as keyof typeof request]: firstName,
                    ['last_name' as keyof typeof request]: lastName,
                    ['account_number' as keyof typeof request]: accountNumber
                };
            }

            API.makeSafeChargeWithdraw(request)
                .then((response: AxiosResponse) => handleSuccessResponse(response))
                .catch((e) => {
                    setInProgress(false);
                    ErrorHandler(e,`post${APILink.MAKE_SAFECHARGE_WITHDRAW}`, toast, handleError)
                })
        } else setInProgress(false);
    }, [firstName, lastName, method, validated, amount, currency, country, accountNumber, email, broker]);

    const submit = useCallback(() => {
        setInProgress(true);
        const inputs = [
            {
                key: 'method', label: t('admin.withdraws.payment_method'), type: InputType.select, options: [
                    {id: "payretailer", name: "payretailer"},
                    {id: "pix", name: "pix"},
                    {id: "latam", name: "latam"},
                ]
            },
            {key: 'country', label: t('admin.withdraws.country'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'currency', label: t('admin.withdraws.currency'), type: InputType.text, regex: '^\\d{1,3}$'},
            {key: 'amount', label: t('admin.withdraws.amount'), type: InputType.number, regex: '^\\d{1,18}$'},
        ];
        let data = {method, country, currency, amount};

        if (method === 'pix') {
            inputs.push({key: 'email', label: t('admin.withdraws.email'), type: InputType.text, regex: emailRegex})
            inputs.push({key: 'first_name', label: t('admin.withdraws.first_name'), type: InputType.text, regex: '^.{3,}$'})
            inputs.push({key: 'last_name', label: t('admin.withdraws.last_name'), type: InputType.text, regex: '^.{3,}$'})
            inputs.push({key: 'account_number', label: t('admin.withdraws.account_number'), type: InputType.text, regex: '^.{10,}$'})

            data = {
                ...data,
                ['email' as keyof typeof data]: email,
                ['first_name' as keyof typeof data]: firstName,
                ['last_name' as keyof typeof data]: lastName,
                ['account_number' as keyof typeof data]: accountNumber
            };
        }
        dispatch(validateData({
            payload: {
                inputs: inputs,
                introducedData: data,
                callback: callback
            }
        }));

    }, [amount, method, country, currency, firstName, lastName, email, accountNumber, callback]);


    const countryUI = useMemo(() => {
        if (!method || !countriesList)
            return <></>;

        const allowedMethod = allowedMethodData.find(m => m.code === method);
        let countries: FooterListItem[] = [];
        if (allowedMethod) {
            countries = countriesList.data.filter(c => allowedMethod.countries.includes(c.code));
        } else countries = countriesList.data;
        return <div className="max-w-md mt-4">
            <DropdownInput
                list={countries.map((c) => {
                    return {
                        name: c.name, id: c.id, icon: <CountryAvatar country={c as Country} withName={false}
                                                                     className="h-3 max-w-[1rem]"/>
                    }
                })}
                name={t('admin.withdraws.country')} selected={country} searchable={true} onChange={changeCountry}/>
        </div>
    }, [method, country, changeCountry, countriesList, changeMethod]);

    const currencyUI = useMemo(() => {
        if (!country || !countriesList || !system || !system.system || !system.system.currencies)
            return <></>

        const selectedCountry = countriesList.data.find(c => c.id === country);
        if (!selectedCountry)
            return <></>;

        let allowedCurrencies = selectedCountry.currencies.map(i => parseInt(i));
        const allowedMethod = allowedMethodData.find(m => m.code === method);
        if (allowedMethod) {
            allowedCurrencies = system.system.currencies.filter(c => allowedMethod.currencies.includes(c.code)).map(c => c.id)
        }

        return <div className="max-w-md mt-4">
            <DropdownInput
                list={system.system.currencies.filter(c => allowedCurrencies.includes(c.id)).map((c) => {
                    return {
                        name: c.code, id: c.id
                    }
                })}
                name={t('admin.withdraws.currency')} selected={currency} searchable={true} onChange={changeCurrency}/>
        </div>
    }, [country, countriesList, system, changeCurrency, currency]);

    const amountUI = useMemo(() => {
        if (!currency || !method || !system || !system.system || !system.system.currencies)
            return <></>
        if (method === 'pix' && (!firstName || !lastName || !email || !accountNumber))
            return <></>

        const selectedCurrency = system.system.currencies.find(c => c.id === currency);
        if (!selectedCurrency)
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.amount_in', {currency: selectedCurrency.code})} type="number"
                          validated={validated} value={amount}
                          fieldName="amount"
                          onChange={changeAmount}/>
        </div>
    }, [currency, validated, amount, method, firstName, lastName, email, accountNumber, changeAmount]);

    const firstNameUI = useMemo(() => {
        if (!currency || !method || method !== 'pix')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.first_name')} type="text" validated={validated} value={firstName}
                          fieldName="first_name"
                          onChange={changeFirstName}/>
        </div>
    }, [currency, method, validated, firstName, changeFirstName])

    const lastNameUI = useMemo(() => {
        if (!firstName || !method || method !== 'pix')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.last_name')} type="text" validated={validated} value={lastName}
                          fieldName="last_name"
                          onChange={changeLastName}/>
        </div>
    }, [firstName, method, validated, lastName, changeLastName])

    const emailUI = useMemo(() => {
        if (!lastName || !method || method !== 'pix')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.email')} type="email" validated={validated} value={email}
                          fieldName="email"
                          onChange={changeEmail}/>
        </div>
    }, [lastName, method, validated, email, changeEmail])

    const accountUI = useMemo(() => {
        if (!email || !method || method !== 'pix')
            return <></>

        return <div className="max-w-md mt-4">
            <OutlineInput name={t('admin.withdraws.account_number')} type="text" validated={validated}
                          value={accountNumber}
                          fieldName="account_number"
                          onChange={changeAccountNumber}/>
        </div>
    }, [email, method, validated, accountNumber, changeAccountNumber])

    const submitButtonUI = useMemo(() => {
        if (!amount)
            return <></>;

        return <div className="max-w-md mt-8">
            <Button className="bg-teal-500 text-white"
                    onClick={() => submit()}
                    disabled={isInProgress}>{isInProgress ?
                <Loader/> : t('Submit')}</Button>
        </div>
    }, [amount, isInProgress, submit]);


    useEffect(() => {
        getCountries();
        if (!clientTransactionID) {
            setCountry(0);
            setAmount(0);
            setMethod(undefined)
            setFirstName(undefined)
            setLastName(undefined)
            setEmail(undefined)
            setAccountNumber(undefined)
            setValidated(false);
        } else getClientTransaction();
    }, [broker, clientTransactionID]);

    return <>
        <div className="max-w-md mt-4">
            <DropdownInput name={t('admin.withdraws.payment_method')} className="w-full min-w-[200px]" selected={method}
                           onChange={changeMethod}
                           list={methods}/>
        </div>
        {countryUI}
        {currencyUI}
        {firstNameUI}
        {lastNameUI}
        {emailUI}
        {accountUI}
        {amountUI}
        {submitButtonUI}
    </>
}

export default memo(SafeChargeForm);
